/**
 * "The substance" of a quantity calculation: either a formula the parser reads or a molar
 * mass the reader types. Grams-to-moles, molarity, percent-solution and ppm all start
 * here, so the rules for it live in one place.
 */

import {
  type FormulaError,
  type MolarMass,
  molarMass,
  type ParsedFormula,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

export type SubstanceMode = "formula" | "mass";

export type Substance =
  | {
      kind: "ok";
      /** g/mol */
      molarMass: number;
      /** Present when the substance came from a formula. */
      formula?: ParsedFormula;
      breakdown?: MolarMass;
    }
  | { kind: "empty" }
  | { kind: "formulaError"; error: FormulaError }
  | { kind: "invalidMass" };

/** Read the substance from whichever field the reader is using. */
export function resolveSubstance(
  mode: SubstanceMode,
  formulaText: string,
  molarMassText: string
): Substance {
  if (mode === "formula") {
    if (formulaText.trim() === "") {
      return { kind: "empty" };
    }
    const parsed = parseFormula(formulaText);
    if (!parsed.ok) {
      return { kind: "formulaError", error: parsed.error };
    }
    const breakdown = molarMass(parsed.formula);
    return {
      kind: "ok",
      molarMass: breakdown.total,
      formula: parsed.formula,
      breakdown,
    };
  }
  if (molarMassText.trim() === "") {
    return { kind: "empty" };
  }
  const value = parseMeasuredNumber(molarMassText);
  if (value === null || !(value > 0)) {
    return { kind: "invalidMass" };
  }
  return { kind: "ok", molarMass: value };
}
